import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown, Search, X, Check } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import {
  filterSelectOptions,
  moveHighlight,
  optionDomId,
  type SearchableOption,
} from './searchableSelect.logic';

export type { SearchableOption } from './searchableSelect.logic';

export interface SearchableSelectProps {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  label?: string;
  /** Trigger text when nothing is selected. */
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  clearable?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
  'aria-label'?: string;
}

/**
 * Accessible, dependency-free searchable single-select.
 *
 * A11y: trigger button exposes `aria-haspopup="listbox"` + `aria-expanded`; the
 * search field is a `combobox` wired to the `listbox` via `aria-controls` and
 * `aria-activedescendant`. Keyboard: ↑/↓ move the highlight (wrapping), Enter
 * selects, Escape closes. Closes on outside click. Filtering/highlight math is
 * unit-tested in searchableSelect.logic.ts.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  label,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No matches.',
  clearable = false,
  disabled = false,
  id,
  name,
  className,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const reactId = useId();
  const baseId = id ?? name ?? reactId;
  const listboxId = `${baseId}-listbox`;

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(-1);

  const filtered = useMemo(() => filterSelectOptions(options, query), [options, query]);
  const selected = options.find((o) => o.value === value) ?? null;

  // Close when clicking outside the component.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  // On open: focus the search field. On close: clear query via the choose/setOpen handlers.
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  // Clear query when the dropdown closes.
  function closeDropdown() {
    setOpen(false);
    setQuery('');
    setHighlight(-1);
  }

  // Clamp highlight to the filtered list bounds.
  const clampedHighlight = filtered.length === 0 ? -1 : highlight >= filtered.length ? filtered.length - 1 : highlight;
  // Reset highlight when the dropdown opens.
  const effectiveHighlight = open ? clampedHighlight : -1;

  function choose(option: SearchableOption) {
    onChange(option.value);
    closeDropdown();
  }

  function clear(event: React.MouseEvent) {
    event.stopPropagation();
    onChange('');
  }

  function onTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setOpen(true);
    }
  }

  function onSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlight((h) => moveHighlight(h, 1, filtered.length));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlight((h) => moveHighlight(h, -1, filtered.length));
        break;
      case 'Enter':
        if (effectiveHighlight >= 0 && filtered[effectiveHighlight]) {
          event.preventDefault();
          choose(filtered[effectiveHighlight]);
        }
        break;
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
      default:
        break;
    }
  }

  const activeDescendant =
    open && effectiveHighlight >= 0 && filtered[effectiveHighlight] ? optionDomId(baseId, filtered[effectiveHighlight].value) : undefined;

  return (
    <div ref={containerRef} className={cn('relative w-full', className)}>
      {label && (
        <label htmlFor={baseId} className="mb-1.5 block text-body-sm font-medium text-on-surface">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          type="button"
          id={baseId}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-lg border bg-card px-3 text-left text-data-md text-on-surface shadow-xs',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:bg-surface-variant disabled:opacity-60',
            'border-input',
          )}
        >
          <span className={cn('truncate', selected ? 'text-on-surface' : 'text-on-surface-subtle')}>
            {selected ? selected.label : placeholder}
          </span>
          <span className="flex items-center gap-1">
            {clearable && selected && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear selection"
                onClick={clear}
                className="rounded p-0.5 text-on-surface-variant hover:bg-surface-variant"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-on-surface-variant" aria-hidden="true" />
          </span>
        </button>

        {open && (
          <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-card border border-border bg-popover shadow-surface-2">
            <div className="border-b border-border p-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" aria-hidden="true" />
                <input
                  ref={searchRef}
                  type="text"
                  role="combobox"
                  aria-expanded={open}
                  aria-controls={listboxId}
                  aria-autocomplete="list"
                  aria-activedescendant={activeDescendant}
                  value={query}
                  placeholder={searchPlaceholder}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  className="h-9 w-full rounded-lg border border-input bg-card pl-8 pr-2 text-data-md text-on-surface focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            </div>

            <ul id={listboxId} role="listbox" className="max-h-60 overflow-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-body-sm text-on-surface-variant" role="presentation">
                  {emptyText}
                </li>
              ) : (
                filtered.map((option, index) => {
                  const isSelected = option.value === value;
                  const isHighlighted = index === effectiveHighlight;
                  return (
                    <li
                      key={option.value}
                      id={optionDomId(baseId, option.value)}
                      role="option"
                      aria-selected={isSelected}
                      onMouseEnter={() => setHighlight(index)}
                      onMouseDown={(e) => {
                        // Prevent the search input blur from closing before select.
                        e.preventDefault();
                        choose(option);
                      }}
                      className={cn(
                        'flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-data-md',
                        isHighlighted ? 'bg-primary/10' : '',
                        isSelected ? 'font-medium text-on-surface' : 'text-on-surface',
                      )}
                    >
                      <span className="min-w-0 truncate">
                        {option.label}
                        {option.description && (
                          <span className="ml-2 text-body-sm capitalize text-on-surface-variant">{option.description}</span>
                        )}
                      </span>
                      {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
